import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { API_BASE, SESSION_KEY } from "../../api";

const brand = {
  navy: "#002B5C",
  bright: "#007BFF",
  brightHover: "#0066d4",
  offWhite: "#f5f8fc",
};

const MSG_INVALID = "Invalid username or password.";
const MSG_FIELDS = "Enter username or phone and password.";
const MSG_NETWORK = "Cannot reach server. Try again.";

/**
 * Sign in with username / email / phone + password (POST /login).
 */
export default function Login({ onLoggedIn }) {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!user.trim() || !password) {
      setError(MSG_FIELDS);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: user.trim(), password }),
      });
      let data;
      try {
        data = await res.json();
      } catch {
        setError(MSG_NETWORK);
        return;
      }
      if (!data.success || !data.result?.[0]) {
        setError(MSG_INVALID);
        return;
      }
      const row = data.result[0];
      const session = {
        id: row.id,
        name: row.name,
        type: row.type,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      onLoggedIn(session);
    } catch (err) {
      console.error(err);
      setError(MSG_NETWORK);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={2}
      py={3}
      boxSizing="border-box"
      sx={{
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        "@supports (min-height: 100dvh)": {
          minHeight: "100dvh",
        },
        background: `linear-gradient(180deg, #e8f0f8 0%, #f5f8fc 40%, #ffffff 100%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          width: "100%",
          maxWidth: 400,
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "visible",
          background: "#ffffff",
          borderRadius: "14px",
          boxShadow: "0 12px 40px rgba(0, 43, 92, 0.25)",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Box display="flex" justifyContent="center" mb={1.5} flexShrink={0}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/matrize-brand.png`}
            alt="Matrize"
            sx={{
              maxWidth: "100%",
              height: "auto",
              maxHeight: 64,
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
        <Typography
          variant="h5"
          fontWeight={700}
          color={brand.navy}
          textAlign="center"
          mb={0.25}
          flexShrink={0}
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" }, lineHeight: 1.25 }}
        >
          Sign in
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          mb={2}
          flexShrink={0}
        >
          Email, username, or phone
        </Typography>

        <Box
          role={error ? "alert" : undefined}
          aria-live="polite"
          sx={{
            mb: 2,
            flexShrink: 0,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 1.5,
            py: error ? 1 : 0,
            borderRadius: "8px",
            bgcolor: error ? "#fdeded" : "transparent",
            color: error ? "#b71c1c" : "transparent",
            border: error ? "1px solid #f5c6cb" : "1px solid transparent",
            boxSizing: "border-box",
            transition: "background-color 0.15s, border-color 0.15s",
          }}
        >
          {error ? (
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontSize: "0.8125rem",
                lineHeight: 1.4,
                textAlign: "center",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                width: "100%",
              }}
            >
              {error}
            </Typography>
          ) : null}
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ minWidth: 0, width: "100%", flex: "0 0 auto" }}
        >
          <TextField
            fullWidth
            label="Username or phone"
            name="user"
            autoComplete="username"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            disabled={submitting}
            sx={{
              mb: 1.5,
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                color: brand.navy,
                backgroundColor: brand.offWhite,
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((v) => !v)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    disabled={submitting}
                    sx={{ color: brand.navy }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                color: brand.navy,
                backgroundColor: brand.offWhite,
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              py: 1.25,
              borderRadius: "10px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9375rem",
              backgroundColor: brand.bright,
              "&:hover": { backgroundColor: brand.brightHover },
            }}
          >
            {submitting ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Sign in"
            )}
          </Button>
        </Box>
        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          color="text.secondary"
          sx={{
            mt: 2,
            px: 1,
            lineHeight: 1.45,
            letterSpacing: { xs: "0.04em", sm: "0.08em" },
            textTransform: "uppercase",
            flexShrink: 0,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          Symbol of accuracy
        </Typography>
      </Paper>
    </Box>
  );
}
